import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@/lib/utils'
import { DashboardLayout } from '@/components/layouts/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AlertCircle, ArrowLeft } from 'lucide-react'
import { LoadingSpinner } from '@/components/features/LoadingSpinner'

import { getProjectById, getProjectInvoices } from '@/lib/api/projects'
import { hasActiveFinalInvoice } from '@/lib/invoiceGating'

import type { Project, ProjectInvoicesResponse, PaymentScheduleStepDto } from '@/types'

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatCurrency(n: number): string {
  return n.toLocaleString('fr-BE', { style: 'currency', currency: 'EUR' })
}

function formatPct(n: number): string {
  return `${n.toLocaleString('fr-BE', { maximumFractionDigits: 1 })} %`
}

const STEP_STATUS_LABEL: Record<string, string> = {
  Invoiced: '✅ Facturé',
  Due: '⏳ À facturer',
  Overdue: '🔴 En retard',
  Scheduled: '📅 Planifié',
  DateToBePlanned: '🔘 Date inconnue',
}

// ── Sub-components ────────────────────────────────────────────────────────────

function FinancialSummary({
  totalWithTax,
  totalInvoiced,
  totalRemaining,
  percentageInvoiced,
}: {
  totalWithTax: number
  totalInvoiced: number
  totalRemaining: number
  percentageInvoiced: number
}) {
  return (
    <div className="rounded-lg border bg-card p-4 space-y-2 text-sm">
      <div className="flex justify-between">
        <span className="text-muted-foreground">Total projet (TVAC)</span>
        <span className="font-semibold tabular-nums">{formatCurrency(totalWithTax)}</span>
      </div>
      <div className="flex justify-between items-center">
        <span className="text-muted-foreground">Déjà facturé</span>
        <div className="flex items-center gap-2">
          <span className={cn('font-medium tabular-nums', totalInvoiced > 0 ? 'text-green-700' : 'text-muted-foreground')}>
            {formatCurrency(totalInvoiced)}
          </span>
          {percentageInvoiced > 0 && (
            <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium">
              {formatPct(percentageInvoiced)}
            </span>
          )}
        </div>
      </div>
      <div className="flex justify-between border-t pt-2">
        <span className="font-medium">Restant à facturer</span>
        <span className="font-semibold tabular-nums">{formatCurrency(totalRemaining)}</span>
      </div>
    </div>
  )
}

function ScheduleOverview({ steps }: { steps: PaymentScheduleStepDto[] }) {
  return (
    <div className="rounded-lg border overflow-hidden text-sm">
      <div className="bg-muted/40 px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
        Échéancier de paiement
      </div>
      <div className="divide-y">
        {steps.map((step) => {
          const label = step.invoiceType === 'final' ? 'Solde' : 'Acompte'
          const invoiced = step.status === 'Invoiced'
          return (
            <div
              key={step.id}
              className={cn(
                'flex items-center justify-between px-3 py-2.5',
                invoiced && 'opacity-50'
              )}
            >
              <span className={cn('font-medium', invoiced && 'line-through')}>
                {label} {step.percentage} %
              </span>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-muted-foreground tabular-nums">
                  {formatCurrency(step.suggestedAmount)}
                </span>
                <span className="text-xs w-28 text-right">
                  {STEP_STATUS_LABEL[step.status] ?? step.status}
                </span>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Forms ─────────────────────────────────────────────────────────────────────

const invoiceTypeSchema = z.object({
  invoiceType: z.enum(['deposit', 'final'] as const),
})
type InvoiceTypeFormData = z.infer<typeof invoiceTypeSchema>

const amountStepSchema = z.object({
  amount: z.number().min(1, 'Montant minimum 1,00 €').optional(),
  invoiceDate: z.string().min(1, 'Date requise'),
})
type AmountStepFormData = z.infer<typeof amountStepSchema>

// ── Page ─────────────────────────────────────────────────────────────────────

export default function NewInvoicePage() {
  const navigate = useNavigate()
  const { id: projectId } = useParams<{ id: string }>()
  const [searchParams, setSearchParams] = useSearchParams()

  const stepParam = searchParams.get('step')
  const invoiceTypeParam = searchParams.get('invoice_type')
  const step = stepParam === '2' ? 2 : 1

  const [project, setProject] = useState<Project | null>(null)
  const [invoicesData, setInvoicesData] = useState<ProjectInvoicesResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Type form (step 1)
  const {
    control,
    handleSubmit: handleSubmitType,
    watch: watchType,
    formState: { errors: errorsType },
  } = useForm<InvoiceTypeFormData>({
    resolver: zodResolver(invoiceTypeSchema),
    defaultValues: {
      invoiceType: (invoiceTypeParam as 'deposit' | 'final') || 'deposit',
    },
  })

  const _selectedType = watchType('invoiceType')

  // Amount form (step 2)
  const {
    register: registerAmount,
    handleSubmit: handleSubmitAmount,
    setValue: setAmountValue,
    watch: watchAmount,
    formState: { errors: errorsAmount },
  } = useForm<AmountStepFormData>({
    resolver: zodResolver(amountStepSchema),
    defaultValues: {
      invoiceDate: new Date().toISOString().split('T')[0],
    },
  })

  const chosenTypeForStep2 = (searchParams.get('invoice_type') as 'deposit' | 'final') || 'deposit'

  // ── Load data ──────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!projectId) return
    const load = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const [projectResponse, invoicesResponse] = await Promise.all([
          getProjectById(projectId),
          getProjectInvoices(projectId),
        ])
        if (!projectResponse) {
          setError('Projet introuvable')
          return
        }
        const allowCreateInvoices = projectResponse.permissions?.allowCreateInvoices === true
        if (!allowCreateInvoices) {
          setError("Vous n'avez pas l'autorisation de créer une facture pour ce projet.")
        }
        setProject(projectResponse)
        setInvoicesData(invoicesResponse)
      } catch {
        setError('Erreur lors du chargement du projet')
      } finally {
        setIsLoading(false)
      }
    }
    load()
  }, [projectId])

  // ── Derived values ─────────────────────────────────────────────────────────

  const totalWithTax = invoicesData?.project.totalWithTax ?? project?.totalWithTax ?? 0
  const totalInvoiced = invoicesData?.project.totalInvoiced ?? 0
  const totalRemaining = invoicesData?.project.totalRemaining ?? totalWithTax - totalInvoiced
  const percentageInvoiced =
    invoicesData?.project.percentageInvoiced ??
    (totalWithTax > 0 ? Math.round((totalInvoiced / totalWithTax) * 10000) / 100 : 0)

  const hasFinalInvoice = hasActiveFinalInvoice(invoicesData?.invoices ?? [])

  const quickChoices = useMemo(() => {
    const schedule = invoicesData?.paymentSchedule
    if (schedule && schedule.steps.length > 0) {
      return schedule.steps.map((s) => ({
        label: `${s.invoiceType === 'final' ? 'Solde' : 'Acompte'} — ${s.percentage} %`,
        amount: s.remainingAmount,
        baseAmount: s.suggestedAmount,
        invoiced: s.status === 'Invoiced',
      }))
    }
    return [0.3, 0.5, 0.6].map((rate) => ({
      label: `${Math.round(rate * 100)} %`,
      amount: Math.round(totalWithTax * rate * 100) / 100,
      baseAmount: Math.round(totalWithTax * rate * 100) / 100,
      invoiced: false,
    }))
  }, [invoicesData?.paymentSchedule, totalWithTax])

  // Pre-fill amount from URL param or nextPendingStep
  useEffect(() => {
    if (step !== 2) return
    const amountParam = searchParams.get('amount')
    if (amountParam) {
      const parsed = parseFloat(amountParam)
      if (parsed > 0) {
        setAmountValue('amount', parsed, { shouldValidate: false })
        return
      }
    }
    if (invoicesData?.paymentSchedule?.nextPendingStep) {
      const { remainingAmount } = invoicesData.paymentSchedule.nextPendingStep
      if (remainingAmount > 0) {
        setAmountValue('amount', remainingAmount, { shouldValidate: false })
      }
    }
  }, [step, searchParams, invoicesData?.paymentSchedule?.nextPendingStep, setAmountValue])

  // Live after-invoice calculation (centimes to avoid floating point drift)
  const typedAmountRaw = watchAmount('amount')
  const typedAmount = typeof typedAmountRaw === 'number' && !isNaN(typedAmountRaw) ? typedAmountRaw : 0

  const invoicedSummary = useMemo(() => {
    const amountCents = Math.round(typedAmount * 100)
    const invoicedCents = Math.round(totalInvoiced * 100)
    const totalCents = Math.round(totalWithTax * 100)
    const afterCents = invoicedCents + amountCents
    const pctAfter = totalCents > 0 ? Math.round((afterCents / totalCents) * 10000) / 100 : 0
    const remainingCents = Math.max(0, totalCents - afterCents)
    return {
      newTotalInvoiced: afterCents / 100,
      pctAfter,
      remaining: remainingCents / 100,
    }
  }, [typedAmount, totalInvoiced, totalWithTax])

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleBackToProject = () => {
    if (!projectId) return
    navigate(`/projects/${projectId}?tab=invoices`)
  }

  const goToStep1 = () => {
    const p = new URLSearchParams(searchParams)
    p.set('step', '1')
    setSearchParams(p)
  }

  const onSubmitType = (data: InvoiceTypeFormData) => {
    if (!projectId) return
    const nextParams = new URLSearchParams(searchParams)
    nextParams.set('step', '2')
    nextParams.set('invoice_type', data.invoiceType)
    setSearchParams(nextParams)
  }

  const handleQuickChoice = (amount: number) => {
    setAmountValue('amount', amount, { shouldValidate: true })
  }

  const onSubmitAmount = async (data: AmountStepFormData) => {
    if (!projectId || isSubmitting) return
    const chosenType = (searchParams.get('invoice_type') as 'deposit' | 'final') || 'deposit'
    if (chosenType === 'deposit' && (!data.amount || data.amount < 1)) return

    try {
      setIsSubmitting(true)
      setError(null)
      const params = new URLSearchParams()
      params.set('invoice_type', chosenType)
      params.set('invoice_date', data.invoiceDate)
      if (chosenType === 'deposit' && data.amount) {
        params.set('amount', String(data.amount))
      }
      navigate(`/projets/${projectId}/invoices/preview?${params.toString()}`)
    } catch {
      setError('Erreur lors de la génération de la facture')
    } finally {
      setIsSubmitting(false)
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <LoadingSpinner />
        </div>
      </DashboardLayout>
    )
  }

  if (error || !project) {
    return (
      <DashboardLayout>
        <div className="max-w-xl mx-auto py-10 px-4 space-y-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Impossible de continuer</AlertTitle>
            <AlertDescription>{error || 'Projet introuvable'}</AlertDescription>
          </Alert>
          <div className="flex justify-end">
            <Button variant="outline" onClick={handleBackToProject}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour au projet
            </Button>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  const scheduleSteps = invoicesData?.paymentSchedule?.steps ?? []

  return (
    <DashboardLayout>
      <div className="max-w-xl mx-auto py-6 px-4 space-y-5">
        {/* Back link */}
        <button
          type="button"
          onClick={handleBackToProject}
          className="flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Retour au projet
        </button>

        {/* Page title */}
        <h1 className="text-xl font-semibold">
          Nouvelle facture — {project.reference}
        </h1>

        {/* ── STEP 1 ── */}
        {step === 1 && (
          <form onSubmit={handleSubmitType(onSubmitType)} className="space-y-5">
            {/* Financial summary */}
            <FinancialSummary
              totalWithTax={totalWithTax}
              totalInvoiced={totalInvoiced}
              totalRemaining={totalRemaining}
              percentageInvoiced={percentageInvoiced}
            />

            {/* Schedule overview */}
            {scheduleSteps.length > 0 && <ScheduleOverview steps={scheduleSteps} />}

            {/* Type selection */}
            <div className="space-y-3">
              <h2 className="text-base font-semibold">Quel type de facture ?</h2>

              <Controller
                control={control}
                name="invoiceType"
                render={({ field }) => (
                  <RadioGroup
                    value={field.value}
                    onValueChange={field.onChange}
                    className="space-y-2.5"
                  >
                    {/* Deposit */}
                    <Label
                      htmlFor="invoice-type-deposit"
                      className={cn(
                        'flex items-start gap-3 rounded-lg border-2 p-4 cursor-pointer transition-colors',
                        field.value === 'deposit'
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-muted-foreground/40'
                      )}
                    >
                      <RadioGroupItem
                        id="invoice-type-deposit"
                        value="deposit"
                        data-testid="invoice-type-deposit"
                        className="mt-[2px]"
                      />
                      <div className="space-y-0.5 min-w-0">
                        <p className="text-sm font-medium">Facture d'acompte</p>
                        <p className="text-xs text-muted-foreground">
                          Paiement partiel avant le solde final — montant libre
                        </p>
                      </div>
                    </Label>

                    {/* Final */}
                    <Label
                      htmlFor="invoice-type-final"
                      className={cn(
                        'flex items-start gap-3 rounded-lg border-2 p-4 transition-colors',
                        hasFinalInvoice
                          ? 'opacity-50 cursor-not-allowed'
                          : 'cursor-pointer',
                        !hasFinalInvoice && field.value === 'final'
                          ? 'border-violet-500 bg-violet-50/50'
                          : 'border-border hover:border-muted-foreground/40'
                      )}
                    >
                      <RadioGroupItem
                        id="invoice-type-final"
                        value="final"
                        data-testid="invoice-type-final"
                        className="mt-[2px]"
                        disabled={hasFinalInvoice}
                      />
                      <div className="space-y-0.5 min-w-0">
                        <p className="text-sm font-medium">Facture de solde</p>
                        {hasFinalInvoice ? (
                          <p className="text-xs text-muted-foreground">
                            Déjà émise pour ce projet
                          </p>
                        ) : totalWithTax > 0 && totalRemaining === 0 ? (
                          <p
                            className="text-xs text-muted-foreground"
                            data-testid="invoice-final-zero-hint"
                          >
                            Facture de clôture à{' '}
                            <span className="font-medium text-foreground">0,00&nbsp;€</span>{' '}
                            — reprend l'historique complet (acomptes et notes de crédit déduits)
                          </p>
                        ) : (
                          <p className="text-xs text-muted-foreground">
                            Règle le solde de{' '}
                            <span className="font-medium text-foreground">
                              {formatCurrency(totalRemaining)}
                            </span>
                          </p>
                        )}
                      </div>
                    </Label>
                  </RadioGroup>
                )}
              />

              {errorsType.invoiceType && (
                <p className="text-xs text-destructive">{errorsType.invoiceType.message}</p>
              )}
            </div>

            <div className="flex items-center justify-between gap-3 pt-1">
              <Button type="button" variant="outline" onClick={handleBackToProject}>
                Annuler
              </Button>
              <Button type="submit">
                Suivant →
              </Button>
            </div>
          </form>
        )}

        {/* ── STEP 2 ── */}
        {step === 2 && (
          <form onSubmit={handleSubmitAmount(onSubmitAmount)} className="space-y-5">
            {/* Type badge + Modifier link */}
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                  chosenTypeForStep2 === 'final'
                    ? 'bg-violet-100 text-violet-700'
                    : 'bg-slate-100 text-slate-700'
                )}
              >
                {chosenTypeForStep2 === 'final' ? 'Facture de solde' : "Facture d'acompte"}
              </span>
              <button
                type="button"
                onClick={goToStep1}
                className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
              >
                Modifier
              </button>
            </div>

            {/* Financial summary */}
            <FinancialSummary
              totalWithTax={totalWithTax}
              totalInvoiced={totalInvoiced}
              totalRemaining={totalRemaining}
              percentageInvoiced={percentageInvoiced}
            />

            {/* Amount section (deposit only) */}
            {chosenTypeForStep2 === 'deposit' && (
              <div className="space-y-4">
                {/* Quick choices */}
                {quickChoices.length > 0 && (
                  <div className="flex gap-2">
                    {quickChoices.map((choice) => {
                      const hasPartialPayment = !choice.invoiced && choice.amount !== choice.baseAmount
                      return (
                        <button
                          key={choice.label}
                          type="button"
                          disabled={choice.invoiced}
                          onClick={() => !choice.invoiced && handleQuickChoice(choice.amount)}
                          className={cn(
                            'flex-1 flex flex-col items-start gap-0.5 px-3 py-2.5 text-sm rounded-lg border transition-all text-left',
                            choice.invoiced
                              ? 'opacity-50 cursor-not-allowed bg-muted/30'
                              : 'hover:bg-muted/50 active:scale-95 active:bg-muted'
                          )}
                        >
                          <span className="font-medium">
                            {choice.label}
                          </span>
                          <span className="tabular-nums text-xs flex items-center gap-1.5">
                            {choice.invoiced ? (
                              <span className="text-muted-foreground line-through">
                                {formatCurrency(choice.baseAmount)}
                              </span>
                            ) : hasPartialPayment ? (
                              <>
                                <span className="text-muted-foreground line-through">
                                  {formatCurrency(choice.baseAmount)}
                                </span>
                                <span className="font-medium text-foreground">
                                  {formatCurrency(choice.amount)}
                                </span>
                              </>
                            ) : (
                              <span className="text-muted-foreground">
                                {choice.amount > 0 ? formatCurrency(choice.amount) : '—'}
                              </span>
                            )}
                          </span>
                        </button>
                      )
                    })}

                  </div>
                )}

                {/* Custom amount */}
                <div className="space-y-1.5">
                  <Label htmlFor="amount">Montant personnalisé</Label>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    min={1}
                    max={totalRemaining}
                    data-testid="invoice-amount-input"
                    className="h-12 text-lg font-medium"
                    {...registerAmount('amount', { valueAsNumber: true })}
                  />

                  {/* Hint + live % summary */}
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">
                      Maximum : {formatCurrency(totalRemaining)}
                    </p>
                    {typedAmount > 0 && totalWithTax > 0 && (
                      <p className="text-xs italic tabular-nums">
                        {/* slate-500 (4.8:1), not slate-400 (2.6:1): this is the share already
                            invoiced, sitting next to a readable orange figure. At 400 the pair
                            read as one live number and one ghost. */}
                        <span className="text-slate-500">{formatPct(percentageInvoiced)}</span>
                        <span className="text-muted-foreground"> → </span>
                        <span className="text-orange-400 font-medium">{formatPct(invoicedSummary.pctAfter)}</span>
                      </p>
                    )}
                  </div>

                  {/* Segmented progress bar */}
                  {totalWithTax > 0 && (
                    <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden flex">
                      {/* slate-500 on the slate-100 track is 4.4:1; slate-300 was 1.36:1, so the
                          money ALREADY billed looked like empty capacity next to the orange share
                          being typed. A progress meter is a graphical object: WCAG 1.4.11 wants
                          3:1 against its track. */}
                      <div
                        className="bg-slate-500 h-1.5 shrink-0 transition-all duration-200"
                        style={{ width: `${Math.min(100, percentageInvoiced)}%` }}
                      />
                      {typedAmount > 0 && (
                        <div
                          className="bg-orange-300 h-1.5 shrink-0 transition-all duration-200"
                          style={{ width: `${Math.min(100 - percentageInvoiced, Math.max(0, invoicedSummary.pctAfter - percentageInvoiced))}%` }}
                        />
                      )}
                    </div>
                  )}



                  {errorsAmount.amount && (
                    <p className="text-xs text-destructive">{errorsAmount.amount.message}</p>
                  )}
                </div>
              </div>
            )}

            {/* Final invoice info */}
            {chosenTypeForStep2 === 'final' && (
              <div className="rounded-lg border bg-violet-50/40 p-4 text-sm space-y-1">
                <p className="font-medium text-violet-900">Montant calculé automatiquement</p>
                <p className="text-muted-foreground">
                  La facture de solde couvrira le restant de{' '}
                  <span className="font-semibold text-foreground">
                    {formatCurrency(totalRemaining)}
                  </span>
                  .
                </p>
              </div>
            )}

            {/* Date */}
            <div className="space-y-1.5">
              <Label htmlFor="invoiceDate">Date de la facture</Label>
              <Input
                id="invoiceDate"
                type="date"
                data-testid="invoice-date-input"
                {...registerAmount('invoiceDate')}
              />
              {errorsAmount.invoiceDate && (
                <p className="text-xs text-destructive">{errorsAmount.invoiceDate.message}</p>
              )}
            </div>

            <div className="flex items-center justify-between gap-3 pt-1">
              <Button type="button" variant="outline" onClick={goToStep1}>
                ← Retour
              </Button>
              <Button type="submit" data-testid="invoice-submit-btn" disabled={isSubmitting}>
                {isSubmitting ? 'Ouverture…' : "Voir l'aperçu"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </DashboardLayout>
  )
}
